import { getApiBaseUrl } from '../config/api';

// FEAT-061 — Caregiver Trust & Safety Onboarding content (web client slice).
//
// Thin client over the auth-gated FEAT-061 API. The eight §8 trust-and-safety
// basics (MVP-8.20…8.27) are fetched from `/trust-safety/content`; the
// caregiver's acknowledgement status is read from
// `/trust-safety/acknowledgement` (so the onboarding step can be skipped once
// acknowledged for the current content version) and recorded by POSTing the
// full topic-key set + the current contentVersion.

export interface TrustSafetyTopic {
  /** Stable machine key the client renders and POSTs back to acknowledge. */
  key: string;
  title: string;
  summary: string;
  body: string;
  /** The single MVP id this topic satisfies (MVP-8.20 … MVP-8.27). */
  mvpRef: string;
}

export interface TrustSafetyContent {
  contentVersion: string;
  topics: TrustSafetyTopic[];
}

export interface TrustSafetyAcknowledgementStatus {
  acknowledged: boolean;
  contentVersion: string;
  acknowledgedAt: string | null;
  acknowledgedTopics: string[];
  requiredTopics: string[];
}

export interface TrustSafetyAcknowledgement {
  id: string;
  userId: string;
  contentVersion: string;
  acknowledgedTopics: string[];
  acknowledgedAt: string;
}

class TrustSafetyService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Fetch the eight trust-and-safety topics + the current content version.
   */
  async getTrustSafetyContent(): Promise<TrustSafetyContent> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/trust-safety/content`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.error || 'Failed to load trust & safety content');
    }

    const result = await response.json();
    const payload = (result.data ?? result) as TrustSafetyContent;
    return {
      contentVersion: payload.contentVersion,
      topics: payload.topics ?? [],
    };
  }

  /**
   * Read the caregiver's acknowledgement status for the CURRENT content
   * version. `acknowledged` is true only when a row exists for that version,
   * so a content revision flips everyone back to un-acknowledged.
   */
  async getAcknowledgement(): Promise<TrustSafetyAcknowledgementStatus> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/trust-safety/acknowledgement`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.error || 'Failed to load acknowledgement status');
    }

    const result = await response.json();
    const payload = (result.data ?? result) as TrustSafetyAcknowledgementStatus;
    return {
      acknowledged: payload.acknowledged === true,
      contentVersion: payload.contentVersion,
      acknowledgedAt: payload.acknowledgedAt ?? null,
      acknowledgedTopics: payload.acknowledgedTopics ?? [],
      requiredTopics: payload.requiredTopics ?? [],
    };
  }

  /**
   * Record an acknowledgement. `acknowledgedTopics` MUST contain every topic
   * key for the current version, or the API rejects with 400
   * INCOMPLETE_ACKNOWLEDGEMENT; a stale contentVersion is rejected with 409.
   * Idempotent per (user, version): first ack is 201, repeats are 200.
   */
  async acknowledge(
    contentVersion: string,
    acknowledgedTopics: string[],
  ): Promise<TrustSafetyAcknowledgement> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/trust-safety/acknowledgement`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ contentVersion, acknowledgedTopics }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.error?.message || error.error || 'Failed to record your acknowledgement',
      );
    }

    const result = await response.json();
    return (result.data ?? result) as TrustSafetyAcknowledgement;
  }
}

export const trustSafetyService = new TrustSafetyService();
